import { useTranslation } from "react-i18next";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NavUserMenuContent, userInitials, type NavUserInfo } from "@/components/nav-user";
import type { MembershipRole } from "@/types/businessMember";

/**
 * The account block at the right of the desktop top bar: avatar 44 px, name
 * and role, opening the same menu as the sidebar footer row.
 */
export function ShellUserMenu({
  user,
  role,
  onSignOut,
}: {
  user: NavUserInfo;
  role: MembershipRole | null;
  onSignOut: () => void;
}) {
  const { t } = useTranslation("nav");
  const initials = userInitials(user.name);
  const roleLabel = role ? t(`memberRoles.${role}`) : "";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-[11px] rounded-g-md py-1 pl-1 pr-2 text-left transition-colors duration-150 ease-out hover:bg-g-primary-wash focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-g-ring"
          aria-label={user.name}
          data-testid="shell-user-menu"
        >
          <Avatar className="h-11 w-11 rounded-g-pill border-2 border-g-bg-card shadow-g-card">
            <AvatarImage src={user.avatar} alt="" />
            <AvatarFallback className="rounded-g-pill bg-g-primary-tint text-[14px] font-extrabold text-g-primary-press">
              {initials}
            </AvatarFallback>
          </Avatar>
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="truncate text-[14px] font-semibold text-g-ink-900">
              {user.name}
            </span>
            {roleLabel ? (
              <span className="truncate text-[12px] text-g-ink-400">
                {roleLabel}
              </span>
            ) : null}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-56 rounded-lg" side="bottom" align="end" sideOffset={6}>
        <NavUserMenuContent user={user} onSignOut={onSignOut} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
